/**
 * Spec 106 — Response-Level Persona-Fit. Typed client for brand-scoped finding
 * routes + the compile-rubric contract endpoint.
 *
 *   POST /api/v1/brands/:brandId/persona-evaluations
 *   GET  /api/v1/brands/:brandId/persona-evaluations
 *   GET  /api/v1/brands/:brandId/persona-evaluations/:findingId
 */
import { apiGet } from '../lib/api.js';
import { apiPost } from '../lib/api-mutations.js';
import type { PersonaConfidence, PersonaType } from './personas.js';

export type ScoredDimensionType = 'priority' | 'wellbeing' | 'trust' | 'anti_signal';
export type FindingStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'insufficient_data'
  | 'failed';

export interface WilsonInterval {
  method: 'wilson';
  level: number;
  low: number;
  high: number;
  half_width: number;
}

export interface ResponseEvidenceStrength {
  sample_size: number;
  responses_with_brand: number;
  ci: WilsonInterval | null;
}

export interface PersonaDimensionResult {
  id: string;
  finding_id: string;
  dimension_type: ScoredDimensionType;
  dimension_label: string;
  score: number;
  reasoning: string | null;
  supporting_quote: string | null;
  response_id: string | null;
  source_dimension_id: string | null;
  weight: number;
}

export interface PersonaResponseFinding {
  id: string;
  persona_id: string;
  persona_stable_id: string;
  persona_version: number;
  persona_confidence_at_eval: PersonaConfidence;
  persona_type: PersonaType;
  brand_id: string;
  query_cluster_id: string;
  status: FindingStatus;
  observed_visibility: number | null;
  response_evidence_strength: ResponseEvidenceStrength | null;
  priority_fit: number | null;
  wellbeing_fit: number | null;
  trust: number | null;
  anti_signal_penalty: number | null;
  rubric_version: string | null;
  judge_model_version: string | null;
  lens_label: string;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  dimension_results?: PersonaDimensionResult[];
}

export async function listPersonaFindings(
  brandId: string,
  filters: { personaId?: string; status?: string } = {},
): Promise<PersonaResponseFinding[]> {
  const params = new URLSearchParams();
  if (filters.personaId) params.set('persona_id', filters.personaId);
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiGet<{ data: PersonaResponseFinding[] }>(
    `/api/v1/brands/${brandId}/persona-evaluations${qs}`,
  );
  return Array.isArray(res.data) ? res.data : [];
}

export async function getPersonaFinding(
  brandId: string,
  findingId: string,
): Promise<PersonaResponseFinding> {
  const res = await apiGet<{ data: PersonaResponseFinding }>(
    `/api/v1/brands/${brandId}/persona-evaluations/${findingId}`,
  );
  return res.data;
}

export async function runPersonaEvaluation(
  brandId: string,
  input: { persona_id: string; query_cluster_id: string; force_refresh?: boolean },
): Promise<{ finding_id: string; status: FindingStatus }> {
  const res = await apiPost<{ data: { finding_id: string; status: FindingStatus } }>(
    `/api/v1/brands/${brandId}/persona-evaluations`,
    input,
  );
  return res.data;
}
